import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// No interceptor needed — cookies are sent automatically via withCredentials

// Auth-check endpoints that should NOT trigger redirect on 401
const AUTH_CHECK_URLS = ['/auth/me', '/auth/login', '/auth/logout', '/employee-portal/verify', '/employee-portal/login', '/employee-portal/logout'];

// Handle 401 responses — redirect to login only for non-auth-check endpoints
// AND only when we're inside the authenticated app shell. Public share routes
// under `/r/*` (e.g. /r/medical?token=...) intentionally allow a nurse with
// no admin session to land directly — a 401 there means the share token is
// expired/invalid, and the page renders an inline error instead of bouncing.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      const isAuthCheck = AUTH_CHECK_URLS.some(u => reqUrl.includes(u));
      if (!isAuthCheck) {
        const path = window.location.pathname;
        const isPublicShare = path.startsWith('/r/') || path.startsWith('/qr/');
        if (!path.includes('/login') && path !== '/' && !isPublicShare) {
          // Clear theme before redirect to ensure clean login page
          localStorage.removeItem("theme");
          document.documentElement.classList.remove("dark");
          // Single unified login page for both admin & employees
          window.location.href = '/login';
        }
      }
    }
    // ── Normalize structured error details ──
    // FastAPI lets endpoints raise HTTPException(detail={...}) with a dict
    // payload (handy for the API, but a footgun for the UI: many catch
    // blocks do `toast.error(err.response.data.detail)` which would then
    // pass an object to sonner → "Objects are not valid as a React child"
    // → entire page renders white. Convert object details to a friendly
    // string here so legacy call-sites stay safe.
    const data = error.response?.data;
    if (data && data.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) {
      const d = data.detail;
      const base = d.message || "Veprimi nuk mund të kryhet";
      const refs = Array.isArray(d.references) ? d.references : [];
      const refList = refs
        .map((r) => `${r.collection || ""}=${r.count ?? ""}`)
        .filter((s) => s !== "=")
        .join(", ");
      data.detail = refList ? `${base} (${refList})` : base;
      // Keep the structured copy under a different key for callers that
      // *want* to read references / ref_summary explicitly.
      data.detail_raw = d;
    }
    return Promise.reject(error);
  }
);

// Auth
export const loginAdmin = (email, password, remember_me = false) => api.post("/auth/login", { email, password, remember_me });
export const registerAdmin = (data) => api.post("/auth/register", data);
export const getMe = () => api.get("/auth/me");
export const logoutAdmin = () => api.post("/auth/logout");

// Employee Portal Auth
export const loginEmployee = (username, password, device_id, remember_me = false) => api.post("/employee-portal/login", { username, password, device_id, remember_me });
export const logoutEmployee = () => api.post("/employee-portal/logout");
export const verifyEmployee = () => api.get("/employee-portal/verify");
export const setEmployeePassword = (new_password) => {
  return api.post("/employee-portal/set-password", { new_password });
};
export const getEmployeePortalDashboard = () => {
  return api.get("/employee-portal/dashboard");
};
export const getEmployeePortalHistory = (month, year) => {
  return api.get(`/employee-portal/history?month=${month}&year=${year}`);
};

// Employee Portal Check In/Out (in-spital presence verification only)
export const employeeCheckIn = (lat, lng, accuracy, samples, extra = {}) => {
  return api.post("/employee-portal/check-in", { lat, lng, accuracy, samples, ...extra });
};
export const employeeCheckOut = (lat, lng, accuracy, samples, extra = {}) => {
  return api.post("/employee-portal/check-out", { lat, lng, accuracy, samples, ...extra });
};

// Employee Portal Leave
export const createLeaveRequest = (data) => {
  return api.post("/employee-portal/leave-request", data);
};
export const getMyLeaveRequests = () => {
  return api.get("/employee-portal/leave-requests");
};

// Employee Portal Profile & PIN
export const getEmployeeProfile = () => {
  return api.get("/employee-portal/profile");
};
export const updateEmployeeProfile = (data) => {
  return api.put("/employee-portal/profile", data);
};
export const changeEmployeePin = (data) => {
  return api.put("/employee-portal/change-pin", data);
};
export const getCheckoutStatus = () => {
  return api.get("/employee-portal/checkout-status");
};
export const requestOvertime = () => {
  return api.post("/employee-portal/request-overtime", {});
};

// Shifts
export const getShifts = () => api.get("/shifts");
export const getShiftSchedule = () => api.get("/shifts/schedule");

// Geofence
export const getGeofenceSettings = () => api.get("/settings/geofence");
export const updateGeofenceSettings = (data) => api.put("/settings/geofence", data);

// Admin Leave Management (2-level workflow)
export const getAllLeaveRequests = (status, department) => {
  let url = "/leave-requests";
  const params = [];
  if (status) params.push(`status=${status}`);
  if (department) params.push(`department=${department}`);
  if (params.length) url += `?${params.join("&")}`;
  return api.get(url);
};
export const coordinatorApproveLeave = (id, note) => api.put(`/leave-requests/${id}/coordinator-approve`, { note: note || "" });
export const coordinatorRejectLeave = (id, note) => api.put(`/leave-requests/${id}/coordinator-reject`, { note: note || "" });
export const hrApproveLeave = (id, note) => api.put(`/leave-requests/${id}/hr-approve`, { note: note || "" });
export const hrRejectLeave = (id, note) => api.put(`/leave-requests/${id}/hr-reject`, { note: note || "" });
export const approveLeave = (id) => api.put(`/leave-requests/${id}/approve`);
export const rejectLeave = (id) => api.put(`/leave-requests/${id}/reject`);
export const getEmployeeLeaveAdmin = (empId) => api.get(`/leave-requests/employee/${empId}`);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");
export const getRecentActivity = () => api.get("/dashboard/recent-activity");
export const getDashboardCharts = () => api.get("/dashboard/charts");

// Employees
export const getEmployees = (opts = {}) => {
  // Backward-compatible: legacy callers pass a bool (= activeOnly).
  // New callers pass { statusFilter, activeOnly }.
  if (typeof opts === "boolean") opts = { activeOnly: opts };
  const { statusFilter, activeOnly = true } = opts;
  const qs = statusFilter
    ? `status_filter=${encodeURIComponent(statusFilter)}`
    : `active_only=${activeOnly}`;
  return api.get(`/employees?${qs}`);
};
export const setEmployeeStatus = (employeeId, status) =>
  api.put(`/employees/${employeeId}/status`, { status });

// Archive
export const getArchivedEmployees = () => api.get("/employees/archive/list");
export const getArchivedEmployeeFull = (empId) => api.get(`/employees/archive/${empId}/full`);
export const restoreEmployee = (empId) => api.put(`/employees/archive/${empId}/restore`);
export const permanentlyDeleteArchivedEmployee = (empId) => api.delete(`/employees/archive/${empId}/permanent`);
export const listPermanentDeletes = () => api.get("/employees/archive/audit/permanent-deletes");
export const reissueEmployeeIdCode = (empId) => api.post(`/employees/${empId}/reissue-id-code`);

// Overtime approvals (admin)
export const getAdminPendingOvertime = () => api.get("/overtime/pending");
export const getRecentOvertime = (limit = 50) => api.get("/overtime/recent", { params: { limit } });
export const approveOvertimeRequest = (id, note) => api.post(`/overtime/${id}/approve`, { note: note || null });
export const rejectOvertimeRequest = (id, note) => api.post(`/overtime/${id}/reject`, { note: note || null });
export const dismissOvertimeRequest = (id, note) => api.post(`/overtime/${id}/dismiss`, { note: note || null });
// Overtime approver configuration (per-department routing)
export const getOvertimeApproverConfig = () => api.get("/overtime/approvers");
export const saveOvertimeApproverConfig = (department, payload) =>
  api.put(`/overtime/approvers/${encodeURIComponent(department)}`, payload);
export const deleteOvertimeApproverConfig = (department) =>
  api.delete(`/overtime/approvers/${encodeURIComponent(department)}`);
export const getMyOvertimePendingCount = () => api.get("/overtime/my-pending-count");

// Admin / SuperAdmin user management
export const listAdminUsers = () => api.get("/admin/users");
export const createAdminUser = (payload) => api.post("/admin/users", payload);
export const updateAdminUser = (id, payload) => api.patch(`/admin/users/${id}`, payload);
export const resetAdminUserPassword = (id, newPassword) =>
  api.post(`/admin/users/${id}/reset-password`, { new_password: newPassword });
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`);
export const getAdminUsersAuditLog = (limit = 200) =>
  api.get(`/admin/users/audit-log?limit=${limit}`);
export const promoteSelfToSuperAdmin = () =>
  api.post(`/admin/users/promote-self-to-superadmin`);

// Shift Groups
export const listShiftGroups = () => api.get("/shift-groups");
export const createShiftGroup = (payload) => api.post("/shift-groups", payload);
export const updateShiftGroup = (id, payload) => api.put(`/shift-groups/${id}`, payload);
export const deleteShiftGroup = (id) => api.delete(`/shift-groups/${id}`);

// Shift Types (CRUD — replaces hard-coded PREDEFINED_SHIFTS)
export const listShiftTypes = (includeInactive = false) =>
  api.get(`/shift-types${includeInactive ? "?include_inactive=true" : ""}`);
export const createShiftType = (payload) => api.post("/shift-types", payload);
export const updateShiftType = (id, payload) => api.put(`/shift-types/${id}`, payload);
export const deleteShiftType = (id) => api.delete(`/shift-types/${id}`);

// ── Scheduling Hub APIs ──
export const listSchedulingTeams = () => api.get("/scheduling-teams");
export const createSchedulingTeam = (payload) => api.post("/scheduling-teams", payload);
export const updateSchedulingTeam = (id, payload) => api.put(`/scheduling-teams/${id}`, payload);
export const deleteSchedulingTeam = (id) => api.delete(`/scheduling-teams/${id}`);
export const getDeptMembersForPlanning = (department) =>
  api.get(`/shifts/employees-for-dept?department=${encodeURIComponent(department)}`);

export const listOfficialHolidays = (year) =>
  api.get(`/official-holidays${year ? `?year=${year}` : ""}`);
export const createOfficialHoliday = (payload) => api.post("/official-holidays", payload);
export const deleteOfficialHoliday = (id) => api.delete(`/official-holidays/${id}`);

export const getSchedulingSettings = () => api.get("/scheduling-settings");
export const updateSchedulingSettings = (payload) => api.put("/scheduling-settings", payload);

export const getCoverageToday = () => api.get("/shifts/coverage/today");
export const copyWeekSchedules = (payload) => api.post("/shifts/schedules/copy-week", payload);

// Swap Workflow v2
export const listShiftSwapsV2 = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && q.append(k, v));
  const qs = q.toString();
  return api.get(`/shift-swaps${qs ? `?${qs}` : ""}`);
};
export const getSwapSuggestions = (employee_id, date, shift_id) => {
  const q = new URLSearchParams({ employee_id, date });
  if (shift_id) q.append("shift_id", shift_id);
  return api.get(`/shift-swaps/suggestions?${q.toString()}`);
};
export const createShiftSwap = (payload) => api.post("/shift-swaps", payload);
export const acceptShiftSwap = (id, note = "") => api.post(`/shift-swaps/${id}/accept`, { note });
export const declineShiftSwap = (id, note = "") => api.post(`/shift-swaps/${id}/decline`, { note });
export const cancelShiftSwap = (id, note = "") => api.post(`/shift-swaps/${id}/cancel`, { note });
export const approveShiftSwap = (id, note = "") => api.post(`/shift-swaps/${id}/approve`, { note });
export const rejectShiftSwap = (id, note = "") => api.post(`/shift-swaps/${id}/reject`, { note });
export const listSwapPayrollLines = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && q.append(k, v));
  const qs = q.toString();
  return api.get(`/shift-swaps/payroll-lines${qs ? `?${qs}` : ""}`);
};
export const markSwapPayrollLinePaid = (id) =>
  api.put(`/shift-swaps/payroll-lines/${id}/mark-paid`);
export const exportSwapPayrollLinesExcel = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && q.append(k, v));
  const qs = q.toString();
  return api.get(`/shift-swaps/payroll-lines/export-excel${qs ? `?${qs}` : ""}`, { responseType: "blob" });
};

// Schedule (weekly grid) — reuse legacy endpoints with new entry_type support
export const listWeekSchedules = (week_start, department) => {
  const q = new URLSearchParams({ week_start });
  if (department) q.append("department", department);
  return api.get(`/shifts/schedules?${q.toString()}`);
};
export const upsertScheduleEntry = (payload) => api.post("/shifts/schedules", payload);
export const deleteScheduleEntry = (id) => api.delete(`/shifts/schedules/${id}`);

// ── Security Policies (Device + GPS) ──
export const getGlobalSecurityDefaults = () => api.get("/security-policies/global");
export const listDepartmentPolicies = () => api.get("/security-policies/departments");
export const upsertDepartmentPolicy = (name, payload) =>
  api.put(`/security-policies/departments/${encodeURIComponent(name)}`, payload);
export const clearDepartmentPolicy = (name) =>
  api.delete(`/security-policies/departments/${encodeURIComponent(name)}`);
export const getEmployeeEffectivePolicy = (id) =>
  api.get(`/security-policies/employees/${id}/effective`);
export const upsertEmployeeSecurityOverride = (id, payload) =>
  api.put(`/security-policies/employees/${id}`, payload);



// Employees Excel / PDF / Import
const _withDept = (path, department) =>
  department ? `${path}?department=${encodeURIComponent(department)}` : path;

export const downloadEmployeesExcel = (department) =>
  api.get(_withDept("/employees/export-excel", department), { responseType: "blob" });
export const downloadEmployeesTemplate = (department) =>
  api.get(_withDept("/employees/template-excel", department), { responseType: "blob" });
export const downloadEmployeesPdf = (department) =>
  api.get(_withDept("/employees/export-pdf", department), { responseType: "blob" });

export const importEmployeesPreview = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/employees/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const importEmployeesConfirm = (file, { force = false } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/employees/import-confirm${force ? "?force=true" : ""}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Roles
export const getRoles = () => api.get("/roles");
export const updateEmployeeRole = (empId, rolesOrRole) => {
  // Accept string (legacy) OR array (new multi-role)
  const payload = Array.isArray(rolesOrRole) ? { roles: rolesOrRole } : { role: rolesOrRole };
  return api.put(`/employees/${empId}/role`, payload);
};
export const getRoleHistory = (empId) => api.get(`/employees/${empId}/role-history`);
export const getDashboardLayout = () => api.get("/auth/dashboard-layout");
export const saveDashboardLayout = (tile_order, hidden_tiles = [], tile_size) => {
  const body = { tile_order, hidden_tiles };
  if (tile_size) body.tile_size = tile_size;
  return api.put("/auth/dashboard-layout", body);
};
export const resetEmployeePassword = (empId, password) => api.put(`/employees/${empId}/reset-password`, { password });

// Login Designer
export const getLoginPagePublished = () => api.get("/login-designer");
export const getLoginPageDraft = () => api.get("/login-designer/draft");
export const saveLoginPageDraft = (settings) => api.put("/login-designer/draft", { settings });
export const publishLoginPage = () => api.post("/login-designer/publish");
export const resetLoginPage = () => api.post("/login-designer/reset");
export const applyLoginPagePreset = (presetId) => api.post(`/login-designer/preset/${presetId}`);
export const uploadLoginAsset = (asset, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/login-designer/upload/${asset}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const deleteLoginAsset = (asset) => api.delete(`/login-designer/upload/${asset}`);

// Employee Portal - Overtime Approvals
export const getPendingOvertime = () => api.get("/employee-portal/pending-overtime");
export const approveOvertimePortal = (reqId, note) => api.put(`/employee-portal/overtime/${reqId}/approve`, { note });
export const rejectOvertimePortal = (reqId, note) => api.put(`/employee-portal/overtime/${reqId}/reject`, { note });
export const getEmployee = (id) => api.get(`/employees/${id}`);
export const createEmployee = (data) => api.post("/employees", data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);

// Attendance
export const pushAttendance = (data) => api.post("/attendance/push", data);
export const getAttendance = (params) => api.get("/attendance", { params });
export const getTodayAttendance = () => api.get("/attendance/today");

// Work Sessions
export const getWorkSessions = (params) => api.get("/work-sessions", { params });

// Payroll
export const getMonthlyPayroll = (month, year) => api.get(`/payroll/monthly?month=${month}&year=${year}`);
export const getEmployeePayroll = (employeeId, month, year) => api.get(`/payroll/employee/${employeeId}?month=${month}&year=${year}`);

// Alerts
export const getAlerts = (date) => api.get(`/alerts${date ? `?date=${date}` : ""}`);

// Export
export const exportPayrollCSV = (month, year) => api.get(`/export/payroll/csv?month=${month}&year=${year}`, { responseType: "blob" });
export const exportPayrollExcel = (month, year) => api.get(`/export/payroll/excel?month=${month}&year=${year}`, { responseType: "blob" });
export const exportPayrollPDF = (month, year) => api.get(`/export/payroll/pdf?month=${month}&year=${year}`, { responseType: "blob" });

// Seed demo data
export const seedDemoData = () => api.post("/seed-demo-data");

// Audit
export const getAuditLogs = () => api.get("/audit-logs");

// Contracts
export const getContracts = (employeeId) => api.get(`/contracts${employeeId ? `?employee_id=${employeeId}` : ""}`);
export const createContract = (data) => api.post("/contracts", data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/contracts/${id}`);

// Personnel Documents
export const getDocuments = (employeeId) => api.get(`/documents${employeeId ? `?employee_id=${employeeId}` : ""}`);
export const getExpiringDocuments = () => api.get("/documents/expiring");
export const createDocument = (data) => api.post("/documents", data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);

// Overtime Requests
export const getOvertimeRequests = (status) => api.get(`/overtime-requests${status ? `?status=${status}` : ""}`);
export const createOvertimeRequest = (data) => api.post("/overtime-requests", data);
export const approveOvertime = (id, note) => api.put(`/overtime-requests/${id}/approve?note=${encodeURIComponent(note || "")}`);
export const rejectOvertime = (id, note) => api.put(`/overtime-requests/${id}/reject?note=${encodeURIComponent(note || "")}`);

// On-Call
export const getOnCallRecords = (month, year) => api.get(`/oncall${month && year ? `?month=${month}&year=${year}` : ""}`);
export const createOnCallRecord = (data) => api.post("/oncall", data);
export const deleteOnCallRecord = (id) => api.delete(`/oncall/${id}`);

// Organogram
export const getOrganogram = () => api.get("/organogram");

// Vacancies
export const getVacancies = (status) => api.get(`/vacancies${status ? `?status=${status}` : ""}`);
export const createVacancy = (data) => api.post("/vacancies", data);
export const updateVacancy = (id, data) => api.put(`/vacancies/${id}`, data);
export const deleteVacancy = (id) => api.delete(`/vacancies/${id}`);

// Applicants
export const getApplicants = (vacancyId) => api.get(`/applicants${vacancyId ? `?vacancy_id=${vacancyId}` : ""}`);
export const createApplicant = (data) => api.post("/applicants", data);
export const updateApplicantStatus = (id, status) => api.put(`/applicants/${id}/status?status=${status}`);
export const deleteApplicant = (id) => api.delete(`/applicants/${id}`);

// Benefits
export const getBenefits = (dept) => api.get(`/benefits${dept ? `?department=${encodeURIComponent(dept)}` : ""}`);
export const createBenefit = (data) => api.post("/benefits", data);
export const deleteBenefit = (id) => api.delete(`/benefits/${id}`);

// Payment List
export const getPaymentList = (month, year) => api.get(`/payment-list?month=${month}&year=${year}`);

// Payroll Analytics & Config
export const getPayrollAnalytics = (month, year) => api.get(`/payroll/analytics?month=${month}&year=${year}`);
export const updateEmployeeSalaryConfig = (empId, config) => api.put(`/payroll/employee/${empId}/salary-config`, config);
export const getWorkSchedules = () => api.get("/work-schedules");
export const saveWorkSchedules = (schedules) => api.post("/work-schedules", schedules);
export const getEmployeeProfileAdmin = (empId) => api.get(`/employees/${empId}/profile`);

// Employee portal
export const changeEmployeePassword = (data) => {
  return api.put("/employee-portal/change-password", data);
};

// Reports (PDF/Excel download)
export const downloadPaymentPDF = (month, year) => api.get(`/reports/payment-pdf?month=${month}&year=${year}`, { responseType: 'blob' });
export const downloadPaymentExcel = (month, year) => api.get(`/reports/payment-excel?month=${month}&year=${year}`, { responseType: 'blob' });
export const downloadEmployeesPDF = (department) => api.get(`/reports/employees-pdf${department ? `?department=${encodeURIComponent(department)}` : ''}`, { responseType: 'blob' });

// Employee Photo
export const uploadEmployeePhoto = (empId, formData) => api.post(`/employees/${empId}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getEmployeePhoto = (empId) => api.get(`/employees/${empId}/photo`);

// Export
export const exportEmployeesExcel = (department) => api.get(`/employees/export/excel${department ? `?department=${encodeURIComponent(department)}` : ''}`, { responseType: 'blob' });
export const exportEmployeesPDF = (department) => api.get(`/employees/export/pdf${department ? `?department=${encodeURIComponent(department)}` : ''}`);
export const exportShiftsExcel = (weekStart, department) => api.get(`/shifts/export/excel?${weekStart ? `week_start=${weekStart}&` : ''}${department ? `department=${encodeURIComponent(department)}` : ''}`, { responseType: 'blob' });

export const uploadEmployeesExcel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/employees/upload-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Department Coordinators
export const getDepartmentCoordinators = () => api.get("/departments/coordinators");
export const setDepartmentCoordinator = (dept, coordinatorId) => api.put(`/departments/${encodeURIComponent(dept)}/coordinator?coordinator_id=${coordinatorId}`);
export const removeDepartmentCoordinator = (dept) => api.delete(`/department-coordinators/${encodeURIComponent(dept)}`);
export const setDepartmentDeputy = (dept, deputyId) => api.put(`/departments/${encodeURIComponent(dept)}/deputy-coordinator?deputy_id=${deputyId}`);

// Salary Grades
export const getSalaryGrades = () => api.get("/salary-grades");
export const createSalaryGrade = (data) => api.post("/salary-grades", data);
export const updateSalaryGrade = (id, data) => api.put(`/salary-grades/${id}`, data);
export const deleteSalaryGrade = (id) => api.delete(`/salary-grades/${id}`);

// Dashboard Alerts
export const getDashboardAlerts = () => api.get("/dashboard/alerts");
export const getShiftBalanceAlerts = () => api.get("/dashboard/shift-balance");

// Department Pay Config
export const getDeptPayConfigs = () => api.get("/department-pay-config");
export const setDeptPayConfig = (data) => api.post("/department-pay-config", data);
export const deleteDeptPayConfig = (dept) => api.delete(`/department-pay-config/${encodeURIComponent(dept)}`);

// Leave Balances
export const getLeaveBalances = () => api.get("/leave-balances");

export default api;

// General Documents (Licenses, permits, etc.)
export const getGeneralDocuments = () => api.get("/general-documents");
export const createGeneralDocument = (data) => api.post("/general-documents", data);
export const deleteGeneralDocument = (id) => api.delete(`/general-documents/${id}`);
export const attachGeneralDocument = (id, formData) => api.post(`/general-documents/${id}/attach`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getExpiringGeneralDocuments = () => api.get("/general-documents/expiring");

// Alternative Work Tracking
export const getAlternativeWork = (params) => api.get("/alternative-work", { params });
export const createAlternativeWork = (data) => api.post("/alternative-work", data);
export const approveAlternativeWork = (id) => api.put(`/alternative-work/${id}/approve`);
export const rejectAlternativeWork = (id) => api.put(`/alternative-work/${id}/reject`);
export const deleteAlternativeWork = (id) => api.delete(`/alternative-work/${id}`);
export const getAlternativeWorkSummary = (month, year) => api.get("/alternative-work/summary", { params: { month, year } });

// Tax Calculator
export const calculateTax = (gross, isDual, dualType) => api.get("/tax/calculate", { params: { gross, is_dual: isDual, dual_type: dualType } });
export const getPayrollWithTax = (month, year) => api.get("/tax/payroll-with-tax", { params: { month, year } });


// Marketing / Referrals
export const getReferrals = (params) => api.get("/referrals", { params });
export const createReferral = (data) => api.post("/referrals", data);
export const approveReferral = (id) => api.put(`/referrals/${id}/approve`);
export const markReferralPaid = (id) => api.put(`/referrals/${id}/paid`);
export const deleteReferral = (id) => api.delete(`/referrals/${id}`);
export const getReferralDoctors = () => api.get("/referrals/doctors");
export const getReferralSummary = (month, year) => api.get("/referrals/summary", { params: { month, year } });
export const getReferralFeeConfig = () => api.get("/referral-fee-config");
export const saveReferralFeeConfig = (data) => api.post("/referral-fee-config", data);

// Notifications
export const getNotifications = () => api.get("/notifications");
export const getUnreadCount = () => api.get("/notifications/unread-count");
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put("/notifications/read-all");

// Organogram Image
export const uploadOrganogramImage = (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/organogram/upload-image", fd); };
export const getOrganogramImage = () => api.get("/organogram/image");
export const deleteOrganogramImage = () => api.delete("/organogram/image");

// Document/Contract Attachments
export const uploadDocumentAttachment = (docId, file) => { const fd = new FormData(); fd.append("file", file); return api.post(`/documents/${docId}/upload-attachment`, fd); };
export const getDocumentAttachment = (docId) => api.get(`/documents/${docId}/attachment`);
export const uploadContractAttachment = (contractId, file) => { const fd = new FormData(); fd.append("file", file); return api.post(`/contracts/${contractId}/upload-attachment`, fd); };
export const getContractAttachment = (contractId) => api.get(`/contracts/${contractId}/attachment`);

// Leave History
export const getEmployeeLeaveHistory = (empId, year) => api.get(`/leave-requests/employee/${empId}/history`, { params: { year } });
export const getEmployeeLeaves = (empId) => api.get(`/leave-requests/employee/${empId}`);

// Department Heads
export const getDepartmentHeads = () => api.get("/department-heads");
export const setDepartmentHead = (dept, data) => api.put(`/department-heads/${encodeURIComponent(dept)}`, data);

// Appointments Calendar
export const getAppointments = (params) => api.get("/appointments", { params });
export const getAppointmentDepartments = () => api.get("/appointments/departments");
export const getMonthlyAppointmentCounts = (year, month) => api.get("/appointments/monthly-counts", { params: { year, month } });
export const createAppointment = (data) => api.post("/appointments", data);
export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, data);
export const cancelAppointment = (id) => api.delete(`/appointments/${id}`);
export const getDailyPrint = (date) => api.get("/appointments/daily-print", { params: { date } });
export const getAppointmentLog = (params) => api.get("/appointments/log", { params });
export const getAppointmentStats = (date) => api.get("/appointments/stats", { params: { date } });

// Employee Portal Appointments
export const empCreateAppointment = (data) => api.post("/employee-portal/appointments", data);
export const empGetAppointments = (params) => api.get("/employee-portal/appointments", { params });

// Orders
export const getOrders = (params) => api.get("/orders", { params });
export const createOrder = (data) => api.post("/orders", data);
export const updateOrderStatus = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const getOrderStats = () => api.get("/orders/stats");
export const getOrderPermissions = () => api.get("/orders/permissions");
export const addOrderPermission = (data) => api.post("/orders/permissions", data);
export const removeOrderPermission = (id) => api.delete(`/orders/permissions/${id}`);

// Employee Portal Orders
export const empGetMyOrderPermissions = () => api.get("/employee-portal/orders/my-permissions");
export const empGetOrders = () => api.get("/employee-portal/orders");
export const empCreateOrder = (data) => api.post("/employee-portal/orders", data);

// Shift Schedule
export const getShiftTypes = () => api.get("/shifts/types");
export const getShiftManagers = () => api.get("/shifts/managers");
export const addShiftManager = (data) => api.post("/shifts/managers", data);
export const removeShiftManager = (id) => api.delete(`/shifts/managers/${id}`);
export const getShiftSchedules = (params) => api.get("/shifts/schedules", { params });
export const createShiftSchedule = (data) => api.post("/shifts/schedules", data);
export const createBulkSchedules = (data) => api.post("/shifts/schedules/bulk", data);
export const deleteShiftSchedule = (id) => api.delete(`/shifts/schedules/${id}`);
export const getShiftSwaps = (params) => api.get("/shifts/swaps", { params });
export const reviewShiftSwap = (id, data) => api.put(`/shifts/swaps/${id}`, data);

// Public Holidays
export const getPublicHolidays = (params) => api.get("/public-holidays", { params });
export const addPublicHoliday = (data) => api.post("/public-holidays", data);
export const deletePublicHoliday = (id) => api.delete(`/public-holidays/${id}`);

// Employee Shift Endpoints
export const getMySchedule = (params) => api.get("/shifts/my-schedule", { params });
export const getMySwaps = () => api.get("/shifts/my-swaps");
export const requestMySwap = (data) => api.post("/shifts/my-swap-request", data);

// Departments
export const getDepartments = () => api.get("/departments");
export const createDepartment = (data) => api.post("/departments", data);
export const updateDepartment = (id, data) => api.put(`/departments/${id}`, data);
export const deleteDepartment = (id) => api.delete(`/departments/${id}`);
export const transferEmployee = (data) => api.post("/departments/transfer", data);
export const transferEmployeesBulk = (data) => api.post("/departments/transfer-bulk", data);

// Department Visual Settings (per-department ambient background)
export const getMyDepartmentVisual = () => api.get("/department-visuals/me");
export const listDepartmentVisuals = () => api.get("/department-visuals");
export const uploadDepartmentVisual = (department, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/department-visuals/${encodeURIComponent(department)}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const deleteDepartmentVisual = (department) =>
  api.delete(`/department-visuals/${encodeURIComponent(department)}`);

// Branding (Login Page background — public GET, admin upload/delete)
export const getLoginBackground = () => api.get("/branding/login-bg");
export const uploadLoginBackground = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/branding/login-bg/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const deleteLoginBackground = () => api.delete("/branding/login-bg");

// Branding (combined fetch for boot — single round-trip for logo+favicon+login_bg)
export const getAllBranding = () => api.get("/branding/all");

// Branding — Hospital Logo
export const getLogoBranding = () => api.get("/branding/logo");
export const uploadLogoBranding = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/branding/logo/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const deleteLogoBranding = () => api.delete("/branding/logo");

// Branding — Favicon
export const getFaviconBranding = () => api.get("/branding/favicon");
export const uploadFaviconBranding = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/branding/favicon/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const deleteFaviconBranding = () => api.delete("/branding/favicon");

// Push Notifications
export const getPushPublicKey = () => api.get("/push/public-key");
export const subscribePush = (subscription, userAgent) => api.post("/push/subscribe", {
  endpoint: subscription.endpoint,
  keys: {
    p256dh: subscription.keys?.p256dh,
    auth: subscription.keys?.auth,
  },
  user_agent: userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : ""),
});
export const unsubscribePush = (endpoint) => api.post("/push/unsubscribe", { endpoint });
export const sendTestPush = () => api.post("/push/test");

// QR Identity
export const scanQr = (token) => api.get(`/qr/${encodeURIComponent(token)}`);
export const regenerateQr = (employeeId) => api.post(`/qr/${employeeId}/regenerate`);
export const getQrAudit = (employeeId) => api.get(`/qr/audit/${employeeId}`);

// Badge Template & Backgrounds
export const getBadgeTemplate = () => api.get("/branding/badge-template");
export const updateBadgeTemplate = (template) => api.put("/branding/badge-template", { template });
export const resetBadgeTemplate = () => api.post("/branding/badge-template/reset");
export const getBadgeFrontBg = () => api.get("/branding/badge-front-bg");
export const uploadBadgeFrontBg = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/branding/badge-front-bg/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const deleteBadgeFrontBg = () => api.delete("/branding/badge-front-bg");
export const getBadgeBackBg = () => api.get("/branding/badge-back-bg");
export const uploadBadgeBackBg = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/branding/badge-back-bg/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const deleteBadgeBackBg = () => api.delete("/branding/badge-back-bg");

// Handoff PDFs (admin-only) — credentials, progress, roadmap
export const regenerateHandoffPdfs = () => api.post("/admin/handoff/regenerate");
export const downloadHandoffPdf = (kind) => {
  // Force fresh build then trigger browser download via a hidden link
  const url = `${API}/admin/handoff/download/${kind}`;
  return api.get(`/admin/handoff/download/${kind}`, { responseType: "blob" }).then((res) => {
    const blob = new Blob([res.data], { type: "application/pdf" });
    const filename = ({
      credentials: "all_credentials_2026.pdf",
      progress: "progres_realizuar.pdf",
      roadmap: "roadmap_dhe_sugjerime.pdf",
    })[kind] || `${kind}.pdf`;
    const dlUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(dlUrl);
    return url;
  });
};

